import { h, ComponentChildren, FunctionComponent } from 'preact';
import { JSX } from 'preact/jsx-runtime';

// Grid 组件属性接口
export interface GridProps extends JSX.HTMLAttributes<HTMLDivElement> {
  children: ComponentChildren;
  fluid?: boolean;
  className?: string;
}

// Row 组件属性接口
export interface RowProps extends JSX.HTMLAttributes<HTMLDivElement> {
  children: ComponentChildren;
  gutter?: number | [number, number]; // 水平和垂直间距
  justify?: 'start' | 'end' | 'center' | 'space-around' | 'space-between';
  align?: 'top' | 'middle' | 'bottom';
  className?: string;
}

// Col 组件属性接口
export interface ColProps extends JSX.HTMLAttributes<HTMLDivElement> {
  children: ComponentChildren;
  span?: number; // 栅格占位格数，0-24
  offset?: number; // 栅格左侧的间隔格数
  xs?: number | ColSize;
  sm?: number | ColSize;
  md?: number | ColSize;
  lg?: number | ColSize;
  xl?: number | ColSize;
  className?: string;
}

interface ColSize {
  span?: number;
  offset?: number;
}

// Grid 组件
export const Grid: FunctionComponent<GridProps> = ({ 
  children, 
  fluid = false, 
  className = '', 
  style = {},
  ...rest 
}) => {
  const gridStyle: JSX.CSSProperties = {
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingLeft: '15px',
    paddingRight: '15px',
    boxSizing: 'border-box',
    maxWidth: fluid ? '100%' : undefined,
    ...(style as JSX.CSSProperties)
  };
  
  // 非流式布局时的响应式宽度通过媒体查询实现
  // 由于内联样式不支持媒体查询，这部分需要在使用时通过JS动态计算或使用CSS-in-JS库
  
  return (
    <div className={className} style={gridStyle} {...rest}>
      {children}
    </div>
  );
};

// Row 组件
export const Row: FunctionComponent<RowProps> = ({ 
  children, 
  gutter = 0, 
  justify = 'start', 
  align = 'top', 
  className = '', 
  style = {},
  ...rest 
}) => {
  // 映射对齐方式到 flex 属性
  const justifyMap: Record<RowProps['justify'] & string, string> = {
    'start': 'flex-start',
    'end': 'flex-end',
    'center': 'center',
    'space-around': 'space-around',
    'space-between': 'space-between'
  };
  
  const alignMap: Record<RowProps['align'] & string, string> = {
    'top': 'flex-start',
    'middle': 'center',
    'bottom': 'flex-end'
  };
  
  const rowStyle: JSX.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    boxSizing: 'border-box',
    justifyContent: justifyMap[justify],
    alignItems: alignMap[align],
    ...(style as JSX.CSSProperties)
  };
  
  if (gutter) {
    const horizontalGutter = Array.isArray(gutter) ? gutter[0] : gutter;
    const verticalGutter = Array.isArray(gutter) ? gutter[1] : 0;
    
    if (horizontalGutter > 0) {
      rowStyle.marginLeft = `-${horizontalGutter / 2}px`;
      rowStyle.marginRight = `-${horizontalGutter / 2}px`;
    }
    
    if (verticalGutter > 0) {
      rowStyle.marginTop = `-${verticalGutter / 2}px`;
      rowStyle.marginBottom = `-${verticalGutter / 2}px`;
    }
  }
  
  // 处理子元素，为Col组件添加gutter
  const processedChildren = gutter ? 
    processChildrenWithGutter(children, gutter) : 
    children;
  
  return (
    <div className={className} style={rowStyle} {...rest}>
      {processedChildren}
    </div>
  );
};

// 处理子元素，为Col组件添加gutter
function processChildrenWithGutter(children: ComponentChildren, gutter: number | [number, number]) {
  if (!children) return null;
  
  const horizontalGutter = Array.isArray(gutter) ? gutter[0] : gutter;
  const verticalGutter = Array.isArray(gutter) ? gutter[1] : 0;
  
  // 如果children是数组，处理每个子元素
  if (Array.isArray(children)) {
    return children.map((child, index) => {
      if (child && typeof child === 'object' && 'type' in child) {
        // 克隆元素并添加padding
        return h(child.type, {
          ...child.props,
          style: {
            ...(child.props.style || {}),
            paddingLeft: horizontalGutter > 0 ? `${horizontalGutter / 2}px` : undefined,
            paddingRight: horizontalGutter > 0 ? `${horizontalGutter / 2}px` : undefined,
            paddingTop: verticalGutter > 0 ? `${verticalGutter / 2}px` : undefined,
            paddingBottom: verticalGutter > 0 ? `${verticalGutter / 2}px` : undefined,
          },
          key: child.key || index
        });
      }
      return child;
    });
  }
  
  // 如果children是单个元素
  if (children && typeof children === 'object' && 'type' in children) {
    return h(children.type, {
      ...children.props,
      style: {
        ...(children.props.style || {}),
        paddingLeft: horizontalGutter > 0 ? `${horizontalGutter / 2}px` : undefined,
        paddingRight: horizontalGutter > 0 ? `${horizontalGutter / 2}px` : undefined,
        paddingTop: verticalGutter > 0 ? `${verticalGutter / 2}px` : undefined,
        paddingBottom: verticalGutter > 0 ? `${verticalGutter / 2}px` : undefined,
      }
    });
  }
  
  return children;
}

// Col 组件
export const Col: FunctionComponent<ColProps> = ({ 
  children, 
  span, 
  offset, 
  xs, 
  sm, 
  md, 
  lg, 
  xl, 
  className = '', 
  style = {},
  ...rest 
}) => {
  const colStyle: JSX.CSSProperties = {
    position: 'relative',
    width: '100%',
    paddingLeft: '15px',
    paddingRight: '15px',
    boxSizing: 'border-box',
    ...(style as JSX.CSSProperties)
  };
  
  // 基础栅格
  if (span !== undefined) {
    const percentage = (span / 24) * 100;
    colStyle.flex = `0 0 ${percentage}%`;
    colStyle.maxWidth = `${percentage}%`;
  }
  
  // 偏移量
  if (offset !== undefined) {
    colStyle.marginLeft = `${(offset / 24) * 100}%`;
  }
  
  // 响应式布局需要通过媒体查询实现
  // 由于内联样式不支持媒体查询，这部分需要在使用时通过JS动态计算
  // 或使用CSS-in-JS库，或在组件挂载时根据窗口大小动态设置样式
  
  return (
    <div className={className} style={colStyle} {...rest}>
      {children}
    </div>
  );
};

// 辅助函数：根据当前窗口宽度计算响应式样式
// 这个函数可以在组件挂载和窗口大小变化时调用
export function getResponsiveStyles(props: ColProps): JSX.CSSProperties {
  const { xs, sm, md, lg, xl } = props;
  const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
  
  const responsiveStyle: JSX.CSSProperties = {};
  
  // 从大到小检查断点，应用最合适的样式
  if (windowWidth >= 1200 && xl) {
    const xlSpan = typeof xl === 'object' ? xl.span : xl;
    const xlOffset = typeof xl === 'object' ? xl.offset : undefined;
    
    if (xlSpan !== undefined) {
      const percentage = (xlSpan / 24) * 100;
      responsiveStyle.flex = `0 0 ${percentage}%`;
      responsiveStyle.maxWidth = `${percentage}%`;
    }
    
    if (xlOffset !== undefined) {
      responsiveStyle.marginLeft = `${(xlOffset / 24) * 100}%`;
    }
  } else if (windowWidth >= 992 && lg) {
    const lgSpan = typeof lg === 'object' ? lg.span : lg;
    const lgOffset = typeof lg === 'object' ? lg.offset : undefined;
    
    if (lgSpan !== undefined) {
      const percentage = (lgSpan / 24) * 100;
      responsiveStyle.flex = `0 0 ${percentage}%`;
      responsiveStyle.maxWidth = `${percentage}%`;
    }
    
    if (lgOffset !== undefined) {
      responsiveStyle.marginLeft = `${(lgOffset / 24) * 100}%`;
    }
  } else if (windowWidth >= 768 && md) {
    const mdSpan = typeof md === 'object' ? md.span : md;
    const mdOffset = typeof md === 'object' ? md.offset : undefined;
    
    if (mdSpan !== undefined) {
      const percentage = (mdSpan / 24) * 100;
      responsiveStyle.flex = `0 0 ${percentage}%`;
      responsiveStyle.maxWidth = `${percentage}%`;
    }
    
    if (mdOffset !== undefined) {
      responsiveStyle.marginLeft = `${(mdOffset / 24) * 100}%`;
    }
  } else if (windowWidth >= 576 && sm) {
    const smSpan = typeof sm === 'object' ? sm.span : sm;
    const smOffset = typeof sm === 'object' ? sm.offset : undefined;
    
    if (smSpan !== undefined) {
      const percentage = (smSpan / 24) * 100;
      responsiveStyle.flex = `0 0 ${percentage}%`;
      responsiveStyle.maxWidth = `${percentage}%`;
    }
    
    if (smOffset !== undefined) {
      responsiveStyle.marginLeft = `${(smOffset / 24) * 100}%`;
    }
  } else if (xs) {
    const xsSpan = typeof xs === 'object' ? xs.span : xs;
    const xsOffset = typeof xs === 'object' ? xs.offset : undefined;
    
    if (xsSpan !== undefined) {
      const percentage = (xsSpan / 24) * 100;
      responsiveStyle.flex = `0 0 ${percentage}%`;
      responsiveStyle.maxWidth = `${percentage}%`;
    }
    
    if (xsOffset !== undefined) {
      responsiveStyle.marginLeft = `${(xsOffset / 24) * 100}%`;
    }
  }
  
  return responsiveStyle;
}

export default {
  Grid,
  Row,
  Col
};
